import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router'
import { Layout } from '@/Core/Layouts/DefaultLayout'
import { isKernelAvailable, registerKernel } from '@/Core/Services/KernelRegistry'
import { kernel } from './Core/Kernel'
import { loggerManager } from '@/Core/Managers'
import { generatedRoutes } from '../.react-router/generated-routes'
import type { IRouteInfo } from '@/Core/Contracts/Loaders/IVitePluginGenerators'
import { initSentry } from '@/Core/Services/sentry'
import { observer } from 'mobx-react-lite'
import { themeStore } from '@/Ship/Stores/ThemeStore'

import 'antd/dist/reset.css'
import '@/Ship/Styles/theme.css'
import '@/Ship/Styles/notifications.css'

// ── Sentry must be initialised BEFORE any React rendering (3.9.5) ──────────
// initSentry() is a no-op when VITE_SENTRY_DSN is not set.
initSentry()

/**
 * Ініціалізація Kernel перед рендерингом
 */
async function initializeKernel() {
    // Реєструємо kernel в глобальному реєстрі якщо ще не зареєстрований
    if (!isKernelAvailable()) {
        registerKernel(kernel)
    }

    // Ініціалізуємо kernel тільки якщо він ще не ініціалізований
    if (!kernel.isKernelInitialized()) {
        await kernel.initialize()
    }
}

/**
 * Root component з Layout та Outlet.
 * observer: читає themeStore.mode, щоб при перемиканні теми перерендерити
 * Core Layout, який розгортає AntdConfig (з геттером theme) у ConfigProvider.
 */
const Root = observer(function Root() {
    // Підписка на тему; саме значення бере геттер AntdConfig.theme
    void themeStore.mode
    return (
        <Layout>
            <Outlet />
        </Layout>
    )
})

/**
 * Створення роутера для SPA з автоматично згенерованими роутами
 * В production використовуються статичні імпорти
 * В development використовуються динамічні lazy імпорти
 */
const routes = generatedRoutes.map((routeInfo: IRouteInfo | (IRouteInfo & { component: any })) => {
    const route: any = {}

    // Перевіряємо чи це production збірка зі статичними імпортами
    if ('component' in routeInfo && routeInfo.component) {
        // PRODUCTION MODE: Статичний імпорт
        route.Component = routeInfo.component
    } else {
        // DEVELOPMENT MODE: Динамічний lazy імпорт
        route.lazy = async () => {
            // Динамічний імпорт компонента (filePath вже відносно src/)
            const module = await import(/* @vite-ignore */ `./${routeInfo.filePath}`)
            return {
                Component: module.default,
            }
        }
    }

    // Використовуємо index тільки для кореневого маршруту "/"
    // Для інших маршрутів, навіть якщо вони IndexPage, використовуємо path
    if (routeInfo.isIndex && routeInfo.path === '/') {
        route.index = true
    } else {
        route.path = routeInfo.path
    }

    return route
})

const router = createBrowserRouter([
    {
        path: '/',
        element: <Root />,
        children: routes,
    },
])

/**
 * Завантаження додатку
 */
initializeKernel()
    .then(() => {
        const root = document.getElementById('root')
        if (root) {
            ReactDOM.createRoot(root).render(
                <React.StrictMode>
                    <RouterProvider router={router} />
                </React.StrictMode>
            )
        } else {
            const errorMsg = 'Failed to find root element. Make sure index.html contains <div id="root"></div>'
            loggerManager.error(errorMsg, 'app-init')
        }
    })
    .catch((error) => {
        loggerManager.error('Failed to initialize application', 'app-init', undefined, { error })

        // Показуємо помилку користувачу
        const root = document.getElementById('root')
        if (root) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
            root.innerHTML = `
                <div style="
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                    color: #ff4d4f;
                    padding: 20px;
                    text-align: center;
                ">
                    <h1>Failed to Initialize Application</h1>
                    <p style="color: #666; max-width: 600px;">
                        ${error instanceof Error ? error.message : 'Unknown error occurred'}
                    </p>
                    <p style="color: #999; font-size: 14px;">
                        Check browser console for more details
                    </p>
                </div>
            `
        }
    })
